/**
 * ServiceQueue - Página de Fila de Atendimento
 * 
 * Permite visualizar e gerenciar todas as conversas aguardando atendimento,
 * incluindo conversas no bot, aguardando agente e transferências pendentes
 * 
 * Suporta duas visualizações: Tabela e Kanban
 * 
 * Arquitetura v5.0.0:
 * - Kanban: useServiceQueueColumn (cursor-based, infinite scroll por coluna)
 * - Tabela: useServiceQueuePaginated (offset-based, paginação tradicional)
 */

import { useState, useMemo, useEffect } from 'react';
import { RefreshCw, Search, X, List, Kanban, ArrowRightLeft, ChevronDown, Check, Users, MessageSquare, Instagram, MessagesSquare, Send, Globe, Inbox, Building, Bot, Clock, Filter, Headphones } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import { SmartAvatar } from '@/components/ui/smart-avatar';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { usePermissions } from '@/hooks/usePermissions';
import { useTenantMembers } from '@/hooks/useTenantMembers';
import { supabase } from '@/integrations/supabase/client';
import { useServiceQueue, QueueSituation, QueueItem, QueueFilters } from '@/hooks/useServiceQueue';
import { useServiceQueuePaginated, PaginatedQueueItem, QueueSortField, SortDirection } from '@/hooks/useServiceQueuePaginated';
import { QueueStatsCards } from '@/components/tenant/queue/QueueStatsCards';
import { QueueTable } from '@/components/tenant/queue/QueueTable';
import { QueueKanbanBoard } from '@/components/tenant/queue/QueueKanbanBoard';
import { AssignConversationDialog } from '@/components/tenant/queue/AssignConversationDialog';
import { ChatConversationModal } from '@/components/tenant/kanban/ChatConversationModal';
import { PaginationFooter } from '@/components/ui/pagination-footer';
import { logger } from '@/lib/logger';
import { cn } from '@/lib/utils';
import { BulkTransferDialog } from '@/components/tenant/transfers/BulkTransferDialog';

// Tipo de visualização
type ViewMode = 'table' | 'kanban';

/** Opções de situação para filtro com ícones */
const situationOptions: { value: QueueSituation | 'all'; label: string; icon: React.ReactNode }[] = [
  { value: 'all', label: 'Todas as situações', icon: <Filter className="h-3 w-3 text-muted-foreground" /> },
  { value: 'waiting_bot', label: 'No Bot', icon: <Bot className="h-3 w-3 text-purple-500" /> },
  { value: 'waiting_agent', label: 'Aguardando Agente', icon: <Users className="h-3 w-3 text-amber-500" /> },
  { value: 'pending_transfer', label: 'Transferências', icon: <ArrowRightLeft className="h-3 w-3 text-amber-500" /> },
  { value: 'in_service', label: 'Em Atendimento', icon: <Headphones className="h-3 w-3 text-green-500" /> },
];

export default function ServiceQueue() {
  const { toast } = useToast();
  const { currentTenant } = useAuth();
  
  // Permissão para transferência em massa: Admin ou Supervisor
  const { isAdmin, isSupervisor } = usePermissions();
  const canBulkTransfer = isAdmin || isSupervisor;
  
  // Hook simplificado v4.0.0 - apenas stats, filtros e ações
  const {
    stats,
    loading,
    filters,
    setFilters,
    refetch,
    assignConversation,
  } = useServiceQueue();

  // Estados locais - viewMode persistido no localStorage
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    const saved = localStorage.getItem('serviceQueue_viewMode');
    return (saved === 'table' || saved === 'kanban') ? saved : 'table';
  });

  // Salva preferência de visualização no localStorage
  const handleViewModeChange = (mode: ViewMode) => {
    setViewMode(mode);
    localStorage.setItem('serviceQueue_viewMode', mode);
  };
  const [departments, setDepartments] = useState<{ id: string; name: string }[]>([]);
  const [inboxes, setInboxes] = useState<{ id: string; name: string; channel_type: string }[]>([]);
  // v2.0.0: Agentes do tenant via hook centralizado (cache compartilhado, RPC server-side)
  const { data: tenantMembers = [] } = useTenantMembers({ onlyActive: true });
  const agents = tenantMembers
    .map(m => ({
      id: m.user_id,
      full_name: m.full_name,
      avatar_url: m.avatar_url,
      email: m.email,
    }))
    .sort((a, b) => (a.full_name || '').localeCompare(b.full_name || ''));
  // Estado para popover do filtro de agente
  const [agentPopoverOpen, setAgentPopoverOpen] = useState(false);
  // Estados de abertura dos popovers de filtros (busca + scroll interno) - padrão Kanban Conversa
  const [deptPopoverOpen, setDeptPopoverOpen] = useState(false);
  const [inboxPopoverOpen, setInboxPopoverOpen] = useState(false);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [selectedConversation, setSelectedConversation] = useState<QueueItem | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [bulkTransferOpen, setBulkTransferOpen] = useState(false);
  
  // Estados para busca expansível (sanfona) - padrão CRM Kanban
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  const [searchValue, setSearchValue] = useState(filters.search || '');

  // Estados para controle do modal de visualização de conversa
  const [chatModalOpen, setChatModalOpen] = useState(false);
  const [viewingConversationId, setViewingConversationId] = useState<string | null>(null);

  // Paginação (apenas para visualização em tabela)
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [sortField, setSortField] = useState<QueueSortField>('last_message_at');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  // Hook paginado para visualização em tabela (v5.0.0)
  const tableQuery = useServiceQueuePaginated(
    filters,
    { page: currentPage, pageSize, sortField, sortDirection },
    { enabled: viewMode === 'table' }
  );

  // Busca departamentos e inboxes para os filtros — 100% via RPC server-side
  // (evita SELECT direto e dependência de RLS pública nessas tabelas)
  useState(() => {
    const fetchFiltersData = async () => {
      if (!currentTenant?.id) return;

      try {
        // Departamentos ativos do tenant ativo (RPC dedicada)
        const { data: deptData, error: deptError } = await supabase
          .rpc('list_tenant_departments_for_filters');

        if (deptError) throw deptError;
        setDepartments((deptData as Array<{ id: string; name: string }>) || []);

        // Inboxes do tenant ativo (reusa RPC já existente get_tenant_inboxes_safe)
        const { data: inboxData, error: inboxError } = await supabase
          .rpc('get_tenant_inboxes_safe');

        if (inboxError) throw inboxError;
        // Mapeia para o formato esperado pelos filtros (id, name, channel_type)
        setInboxes(
          ((inboxData as Array<{ id: string; name: string; channel_type: string }>) || [])
            .map(({ id, name, channel_type }) => ({ id, name, channel_type }))
        );

        // v2.0.0: Agentes vêm via useTenantMembers (hook centralizado acima)

      } catch (error) {
        logger.error('[ServiceQueue] Erro ao buscar dados de filtros:', error);
      }
    };

    fetchFiltersData();
  });

  // Atualiza manualmente (tabela + kanban)
  const handleRefresh = async () => {
    setRefreshing(true);
    await refetch();
    // Também atualiza a tabela se estiver em modo tabela
    if (viewMode === 'table') {
      tableQuery.refetch();
    }
    setRefreshing(false);
    toast({
      title: 'Fila atualizada',
      description: 'Os dados foram atualizados com sucesso',
    });
  };

  // Abre modal de atribuição
  const handleOpenAssign = (item: QueueItem) => {
    setSelectedConversation(item);
    setAssignDialogOpen(true);
  };

  // Abre modal de visualização de conversa
  const handleViewConversation = (item: QueueItem) => {
    setViewingConversationId(item.id);
    setChatModalOpen(true);
  };

  // Fecha modal de visualização e atualiza a lista
  const handleCloseConversationModal = (open: boolean) => {
    setChatModalOpen(open);
    if (!open) {
      setViewingConversationId(null);
      refetch(); // Atualiza a tabela ao fechar
    }
  };

  // Executa atribuição
  const handleAssign = async (conversationId: string, agentId: string): Promise<boolean> => {
    const success = await assignConversation(conversationId, agentId);
    
    if (success) {
      toast({
        title: 'Conversa atribuída',
        description: 'A conversa foi atribuída ao agente com sucesso',
      });
    } else {
      toast({
        title: 'Erro',
        description: 'Não foi possível atribuir a conversa',
        variant: 'destructive',
      });
    }

    return success;
  };

  // Debounce da busca expansível (300ms) - padrão CRM Kanban
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchValue !== (filters.search || '')) {
        setFilters(prev => ({ ...prev, search: searchValue || '' }));
        setCurrentPage(1);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchValue]);

  // Sincroniza busca externa
  useEffect(() => {
    if (filters.search !== searchValue) {
      setSearchValue(filters.search || '');
    }
  }, [filters.search]);

  // Fecha busca ao clicar fora (se vazio)
  const handleSearchBlur = () => {
    if (!searchValue) {
      setIsSearchExpanded(false);
    }
  };

  // Toggle da busca expansível
  const toggleSearch = () => {
    if (isSearchExpanded && !searchValue) {
      setIsSearchExpanded(false);
    } else {
      setIsSearchExpanded(true);
    }
  };

  // Atualiza filtro de situação
  const handleSituationChange = (value: string) => {
    setFilters(prev => ({ 
      ...prev, 
      situation: value as QueueSituation | 'all' 
    }));
    setCurrentPage(1);
  };

  // Atualiza filtro de departamento
  const handleDepartmentChange = (value: string) => {
    setFilters(prev => ({ 
      ...prev, 
      departmentId: value === 'all' ? null : value 
    }));
    setCurrentPage(1);
  };

  /** Retorna label do filtro de departamento selecionado */
  const getDepartmentLabel = () => {
    if (!filters.departmentId) return 'Depto';
    if (filters.departmentId === 'none') return 'Sem Departamento';
    return departments.find(d => d.id === filters.departmentId)?.name || 'Depto';
  };

  // Atualiza filtro de inbox
  const handleInboxChange = (value: string) => {
    setFilters(prev => ({ 
      ...prev, 
      inboxId: value === 'all' ? null : value 
    }));
    setCurrentPage(1);
  };

  // Atualiza filtro de agente
  const handleAgentChange = (value: string) => {
    setFilters(prev => ({ 
      ...prev, 
      agentId: value === 'all' ? null : value 
    }));
    setCurrentPage(1);
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Cabeçalho unificado - Título + Filtros + Ações na mesma linha (padrão CRM Kanban) */}
      <div className="flex items-center gap-2 sm:gap-3 w-full">
        {/* Título */}
        <h1 className="text-lg font-semibold shrink-0 truncate max-w-[120px] sm:max-w-[200px]">
          Fila de Atendimento
        </h1>

        {/* Busca Expansível (Sanfona) - padrão CRM Kanban */}
        <div className="flex items-center shrink-0">
          <div 
            className={cn(
              "flex items-center overflow-hidden transition-all duration-300 ease-out",
              isSearchExpanded ? "w-32 sm:w-44 opacity-100 mr-1" : "w-0 opacity-0"
            )}
          >
            <div className="relative w-full">
              <Input
                placeholder="Buscar..."
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                onBlur={handleSearchBlur}
                className="h-7 w-full text-xs rounded-full pl-3 pr-7"
                autoFocus={isSearchExpanded}
              />
              {searchValue && (
                <button
                  onClick={() => { setSearchValue(''); }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </div>
          </div>
          <Button 
            variant="ghost" 
            size="icon"
            className="h-7 w-7 rounded-full shrink-0"
            onClick={toggleSearch}
          >
            {isSearchExpanded ? <X className="h-4 w-4" /> : <Search className="h-4 w-4" />}
          </Button>
        </div>

        {/* Separador vertical */}
        <Separator orientation="vertical" className="h-5 hidden sm:block shrink-0" />

        {/* Filtros Rápidos - Botões pill-shaped (padrão CRM Kanban) */}
        <div className="flex items-center gap-1.5 sm:gap-2 overflow-x-auto scrollbar-hide">
          {/* Filtro: Situação - com ícones (padrão Kanban Conversa) */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button 
                variant="outline" 
                size="sm"
                className={cn(
                  "h-7 px-2.5 rounded-full text-xs gap-1.5 shrink-0",
                  filters.situation !== 'all' && "border-primary bg-primary/5"
                )}
              >
                {situationOptions.find(o => o.value === filters.situation)?.icon}
                <span>{situationOptions.find(o => o.value === filters.situation)?.label || 'Situação'}</span>
                <ChevronDown className="h-3 w-3 opacity-50" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              {situationOptions.map((opt) => (
                <DropdownMenuItem 
                  key={opt.value} 
                  onClick={() => handleSituationChange(opt.value)}
                  className={cn(filters.situation === opt.value && "bg-accent")}
                >
                  <span className="mr-2">{opt.icon}</span>
                  {opt.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Filtro: Departamento - Popover + Command (busca + scroll interno) */}
          <Popover open={deptPopoverOpen} onOpenChange={setDeptPopoverOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className={cn(
                  "h-7 px-2.5 rounded-full text-xs gap-1.5 shrink-0",
                  filters.departmentId && "border-primary bg-primary/5"
                )}
              >
                <Building className="h-3 w-3" />
                <span>{getDepartmentLabel()}</span>
                <ChevronDown className="h-3 w-3 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[260px] p-0" align="start">
              <Command>
                <CommandInput placeholder="Buscar departamento..." />
                <CommandList>
                  <CommandEmpty>Nenhum departamento encontrado.</CommandEmpty>
                  <CommandGroup>
                    <CommandItem
                      value="__all__"
                      onSelect={() => { handleDepartmentChange('all'); setDeptPopoverOpen(false); }}
                      className="cursor-pointer"
                    >
                      <div className={cn(
                        "mr-2 h-4 w-4 rounded-full border flex items-center justify-center",
                        !filters.departmentId ? "border-primary bg-primary" : "border-muted"
                      )}>
                        {!filters.departmentId && <Check className="h-2.5 w-2.5 text-primary-foreground" />}
                      </div>
                      <Building className="h-4 w-4 mr-2 opacity-50" />
                      <span>Todos deptos</span>
                    </CommandItem>
                    <CommandItem
                      value="__none__"
                      onSelect={() => { handleDepartmentChange('none'); setDeptPopoverOpen(false); }}
                      className="cursor-pointer"
                    >
                      <div className={cn(
                        "mr-2 h-4 w-4 rounded-full border flex items-center justify-center",
                        filters.departmentId === 'none' ? "border-primary bg-primary" : "border-muted"
                      )}>
                        {filters.departmentId === 'none' && <Check className="h-2.5 w-2.5 text-primary-foreground" />}
                      </div>
                      <Building className="h-4 w-4 mr-2 opacity-50" />
                      <span>Sem Departamento</span>
                    </CommandItem>
                  </CommandGroup>
                  {departments.length > 0 && <CommandSeparator />}
                  <CommandGroup heading="Departamentos">
                    {departments.map((dept) => (
                      <CommandItem
                        key={dept.id}
                        value={dept.name}
                        onSelect={() => { handleDepartmentChange(dept.id); setDeptPopoverOpen(false); }}
                        className="cursor-pointer"
                      >
                        <div className={cn(
                          "mr-2 h-4 w-4 rounded-full border flex items-center justify-center",
                          filters.departmentId === dept.id ? "border-primary bg-primary" : "border-muted"
                        )}>
                          {filters.departmentId === dept.id && <Check className="h-2.5 w-2.5 text-primary-foreground" />}
                        </div>
                        <Building className="h-4 w-4 mr-2 text-blue-600" />
                        <span className="truncate">{dept.name}</span>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>

          {/* Filtro: Inbox - Popover + Command (busca + scroll interno) */}
          <Popover open={inboxPopoverOpen} onOpenChange={setInboxPopoverOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className={cn(
                  "h-7 px-2.5 rounded-full text-xs gap-1.5 shrink-0",
                  filters.inboxId && "border-primary bg-primary/5"
                )}
              >
                {(() => {
                  const selected = inboxes.find(i => i.id === filters.inboxId);
                  if (!selected) return <Inbox className="h-3 w-3" />;
                  switch (selected.channel_type) {
                    case 'whatsapp':
                    case 'whatsapp_oficial':
                      return <MessageSquare className="h-3 w-3 text-green-600" />;
                    case 'instagram':
                      return <Instagram className="h-3 w-3 text-pink-500" />;
                    case 'messenger':
                      return <MessagesSquare className="h-3 w-3 text-blue-500" />;
                    case 'telegram':
                      return <Send className="h-3 w-3 text-sky-500" />;
                    case 'webchat':
                      return <Globe className="h-3 w-3 text-muted-foreground" />;
                    default:
                      return <Inbox className="h-3 w-3" />;
                  }
                })()}
                <span>{inboxes.find(i => i.id === filters.inboxId)?.name || 'Inbox'}</span>
                <ChevronDown className="h-3 w-3 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[260px] p-0" align="start">
              <Command>
                <CommandInput placeholder="Buscar inbox..." />
                <CommandList>
                  <CommandEmpty>Nenhuma inbox encontrada.</CommandEmpty>
                  <CommandGroup>
                    <CommandItem
                      value="__all__"
                      onSelect={() => { handleInboxChange('all'); setInboxPopoverOpen(false); }}
                      className="cursor-pointer"
                    >
                      <div className={cn(
                        "mr-2 h-4 w-4 rounded-full border flex items-center justify-center",
                        !filters.inboxId ? "border-primary bg-primary" : "border-muted"
                      )}>
                        {!filters.inboxId && <Check className="h-2.5 w-2.5 text-primary-foreground" />}
                      </div>
                      <Inbox className="h-4 w-4 mr-2 opacity-50" />
                      <span>Todas inboxes</span>
                    </CommandItem>
                  </CommandGroup>
                  {inboxes.length > 0 && <CommandSeparator />}
                  <CommandGroup heading="Inboxes">
                    {inboxes.map((inbox) => (
                      <CommandItem
                        key={inbox.id}
                        value={inbox.name}
                        onSelect={() => { handleInboxChange(inbox.id); setInboxPopoverOpen(false); }}
                        className="cursor-pointer"
                      >
                        <div className={cn(
                          "mr-2 h-4 w-4 rounded-full border flex items-center justify-center",
                          filters.inboxId === inbox.id ? "border-primary bg-primary" : "border-muted"
                        )}>
                          {filters.inboxId === inbox.id && <Check className="h-2.5 w-2.5 text-primary-foreground" />}
                        </div>
                        <span className="mr-2 inline-flex">
                          {(() => {
                            switch (inbox.channel_type) {
                              case 'whatsapp':
                              case 'whatsapp_oficial':
                                return <MessageSquare className="h-3.5 w-3.5 text-green-600" />;
                              case 'instagram':
                                return <Instagram className="h-3.5 w-3.5 text-pink-500" />;
                              case 'messenger':
                                return <MessagesSquare className="h-3.5 w-3.5 text-blue-500" />;
                              case 'telegram':
                                return <Send className="h-3.5 w-3.5 text-sky-500" />;
                              case 'webchat':
                                return <Globe className="h-3.5 w-3.5 text-muted-foreground" />;
                              default:
                                return <Inbox className="h-3.5 w-3.5 text-muted-foreground" />;
                            }
                          })()}
                        </span>
                        <span className="truncate">{inbox.name}</span>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>

          {/* Filtro: Agente - Popover + Command com busca e avatares (padrão CrmSellerFilter) */}
          <Popover open={agentPopoverOpen} onOpenChange={setAgentPopoverOpen}>
            <PopoverTrigger asChild>
              <Button 
                variant="outline" 
                size="sm"
                className={cn(
                  "h-7 px-2.5 rounded-full text-xs gap-1.5 shrink-0",
                  filters.agentId && "border-primary bg-primary/5"
                )}
              >
                {(() => {
                  const selectedAgent = agents.find(a => a.id === filters.agentId);
                  if (selectedAgent) {
                    return (
                      <>
                        <SmartAvatar
                          src={selectedAgent.avatar_url}
                          name={selectedAgent.full_name || 'Agente'}
                          size="xs"
                          use3DFallback
                          className="h-4 w-4"
                        />
                        <span className="truncate max-w-[60px]">{selectedAgent.full_name || 'Agente'}</span>
                      </>
                    );
                  }
                  return (
                    <>
                      <Users className="h-3 w-3 opacity-70" />
                      <span>Agente</span>
                    </>
                  );
                })()}
                <ChevronDown className="h-3 w-3 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[260px] p-0" align="start">
              <Command>
                <CommandInput placeholder="Buscar agente..." />
                <CommandList>
                  <CommandEmpty>Nenhum agente encontrado.</CommandEmpty>
                  <CommandGroup>
                    {/* Opção: Todos agentes */}
                    <CommandItem
                      value="__all__"
                      onSelect={() => { handleAgentChange('all'); setAgentPopoverOpen(false); }}
                      className="cursor-pointer"
                    >
                      <div className={cn(
                        "mr-2 h-4 w-4 rounded-full border flex items-center justify-center",
                        !filters.agentId ? "border-primary bg-primary" : "border-muted"
                      )}>
                        {!filters.agentId && <Check className="h-2.5 w-2.5 text-primary-foreground" />}
                      </div>
                      <Users className="h-4 w-4 mr-2 opacity-50" />
                      <span>Todos Agentes</span>
                    </CommandItem>
                    {/* Opção: Sem agente */}
                    <CommandItem
                      value="__unassigned__"
                      onSelect={() => { handleAgentChange('unassigned'); setAgentPopoverOpen(false); }}
                      className="cursor-pointer"
                    >
                      <div className={cn(
                        "mr-2 h-4 w-4 rounded-full border flex items-center justify-center",
                        filters.agentId === 'unassigned' ? "border-primary bg-primary" : "border-muted"
                      )}>
                        {filters.agentId === 'unassigned' && <Check className="h-2.5 w-2.5 text-primary-foreground" />}
                      </div>
                      <Users className="h-4 w-4 mr-2 opacity-50" />
                      <span>Sem agente</span>
                    </CommandItem>
                  </CommandGroup>
                  <CommandSeparator />
                  <CommandGroup heading="Agentes">
                    {agents.map(agent => (
                      <CommandItem
                        key={agent.id}
                        value={agent.full_name || agent.email || agent.id}
                        onSelect={() => { handleAgentChange(agent.id); setAgentPopoverOpen(false); }}
                        className="cursor-pointer"
                      >
                        <div className={cn(
                          "mr-2 h-4 w-4 rounded-full border flex items-center justify-center",
                          filters.agentId === agent.id ? "border-primary bg-primary" : "border-muted"
                        )}>
                          {filters.agentId === agent.id && <Check className="h-2.5 w-2.5 text-primary-foreground" />}
                        </div>
                        <SmartAvatar
                          src={agent.avatar_url}
                          name={agent.full_name || 'Agente'}
                          size="sm"
                          use3DFallback
                          className="mr-2"
                        />
                        <div className="flex flex-col flex-1 min-w-0">
                          <span className="truncate">{agent.full_name || 'Sem nome'}</span>
                          {agent.email && (
                            <span className="text-xs text-muted-foreground truncate">{agent.email}</span>
                          )}
                        </div>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>

        {/* Espaço flexível para empurrar ações para a direita */}
        <div className="flex-1" />

        {/* Ações - Atualizar, Transferir, Toggle de Visualização */}
        <div className="flex items-center gap-1 sm:gap-2 shrink-0">
          {/* Botão Atualizar */}
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors disabled:opacity-50"
            title="Atualizar"
          >
            <RefreshCw className={cn("h-4 w-4", refreshing && "animate-spin")} />
          </button>
          
          {/* Botão Transferência em Massa */}
          {canBulkTransfer && (
            <button
              onClick={() => setBulkTransferOpen(true)}
              className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
              title="Transferência em Massa"
            >
              <ArrowRightLeft className="h-4 w-4" />
            </button>
          )}

          {/* Toggle de Visualização */}
          <div className="flex items-center bg-muted/50 rounded-lg p-1 border border-border">
            <button
              onClick={() => handleViewModeChange('table')}
              className={cn(
                'flex items-center gap-1.5 px-2 sm:px-3 py-1.5 rounded-md text-sm font-medium transition-all',
                viewMode === 'table'
                  ? 'bg-foreground text-background shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              )}
            >
              <List className="h-4 w-4" />
              <span className="hidden sm:inline">Tabela</span>
            </button>
            <button
              onClick={() => handleViewModeChange('kanban')}
              className={cn(
                'flex items-center gap-1.5 px-2 sm:px-3 py-1.5 rounded-md text-sm font-medium transition-all',
                viewMode === 'kanban'
                  ? 'bg-foreground text-background shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              )}
            >
              <Kanban className="h-4 w-4" />
              <span className="hidden sm:inline">Kanban</span>
            </button>
          </div>
        </div>
      </div>

      {/* Modal de Transferência em Massa - montado somente quando aberto
          para evitar side-effects (queries) ao carregar a fila */}
      {bulkTransferOpen && (
        <BulkTransferDialog
          open={bulkTransferOpen}
          onOpenChange={setBulkTransferOpen}
          context="queue"
          showDealOptions={true}
        />
      )}

      {/* Cards de Estatísticas */}
      <QueueStatsCards stats={stats} loading={loading} />

      {/* Conteúdo - Tabela ou Kanban */}
      {viewMode === 'table' ? (
        <>
          {/* Tabela v5.0.0 - usa hook paginado dedicado */}
          <QueueTable 
            items={tableQuery.items.map(item => ({
              // Converte PaginatedQueueItem para QueueItem (formato esperado pelo componente)
              id: item.id,
              status: item.status as 'pending' | 'open' | 'closed',
              created_at: item.created_at,
              last_message_at: item.last_message_at,
              last_message_preview: item.last_message_preview,
              unread_count: item.unread_count,
              situation: item.situation,
              waiting_time: item.waiting_seconds,
              contact: item.contact_id ? {
                id: item.contact_id,
                name: item.contact_name,
                phone: item.contact_phone,
                avatar_url: item.contact_avatar_url,
              } : null,
              inbox: {
                id: item.inbox_id,
                name: item.inbox_name || 'Inbox',
                identifier: item.inbox_identifier || '',
              },
              department: item.department_id ? {
                id: item.department_id,
                name: item.department_name || '',
              } : null,
              assigned_agent: item.assigned_agent_id ? {
                id: item.assigned_agent_id,
                full_name: item.assigned_agent_name,
                avatar_url: item.assigned_agent_avatar,  // v1.2.0
              } : null,
              pending_transfer: item.has_pending_transfer ? {
                id: item.transfer_id!,
                to_agent_id: item.transfer_to_agent_id,
                to_agent_name: item.transfer_to_agent_name,
                to_department_id: item.transfer_to_department_id,
                to_department_name: item.transfer_to_department_name,
                from_agent_id: item.transfer_from_agent_id,
                from_agent_name: item.transfer_from_agent_name,
                created_at: item.transfer_created_at!,
              } : undefined,
            }))}
            loading={tableQuery.isLoading}
            onAssign={handleOpenAssign}
            onView={handleViewConversation}
          />
          
          {/* Paginação */}
          <PaginationFooter
            totalItems={tableQuery.totalCount}
            currentPage={currentPage}
            pageSize={pageSize}
            onPageChange={setCurrentPage}
            onPageSizeChange={setPageSize}
            pageSizeOptions={[10, 15, 20, 30, 50]}
          />
        </>
      ) : (
        /* Kanban v4.0.0 - cada coluna busca seus próprios dados */
        <QueueKanbanBoard
          filters={filters}
          stats={stats}
          onRefresh={refetch}
        />
      )}

      {/* Modal de Atribuição (apenas para visualização em tabela) */}
      {viewMode === 'table' && (
        <AssignConversationDialog
          open={assignDialogOpen}
          onOpenChange={setAssignDialogOpen}
          conversation={selectedConversation}
          onAssign={handleAssign}
        />
      )}

      {/* Modal de Visualização de Conversa (para visualização em tabela) */}
      {viewMode === 'table' && (
        <ChatConversationModal
          conversationId={viewingConversationId}
          open={chatModalOpen}
          onOpenChange={handleCloseConversationModal}
        />
      )}
    </div>
  );
}
